/**
 * Sunday page sections (sections/sunday-c-*.liquid): the small bits of behaviour they share.
 * - Specs: "All specs" opens the rest on a phone; "Weight to carry" follows the width chosen in the buy hero.
 * - What owners say: "More reviews" on a phone; "Write a review" / "Ask a question" open Judge.me's own
 *   forms. Judge.me's widget waits in a <template>; the first tap adds it (hidden), loads its files the way
 *   Judge.me's own loader does (its core, then the widget), and clicks its button once it appears.
 *   If that does not happen within 8 s, the review panel opens instead. Nothing is sent from here.
 * - Every review (24 Oct, AR): "Read all N reviews" and a link to #reviews-all open a panel over the product
 *   page (snippets/sunday-rv-panel) with Judge.me's full list in Sunday's look (sunday-jm.css, loaded on the
 *   tap) and the price with Add to cart. The reviews page shows its list (Judge.me's all-reviews widget) in
 *   place. One list that keeps loading as you scroll; no rating filter, no sort.
 * - Questions: "All N questions".
 * Loaded once per page however many sections use it.
 */
if (!window.__sdC) {
  window.__sdC = true;
  const fill = (s, v) => String(s || '').replace(/\[(\w+)\]/g, (m, k) => (k in v ? v[k] : m));

  document.addEventListener('click', (e) => {
    const t = e.target.closest && e.target.closest('[data-csp-all], [data-crv-more], [data-cfq-all], [data-crv-write], [data-crv-ask], [data-crv-all]');
    if (!t) return;
    if (t.matches('[data-csp-all]')) {
      t.closest('.sd-csp').classList.add('sd-open');
      t.remove();
    } else if (t.matches('[data-crv-more]')) {
      t.closest('.sd-crv').querySelector('[data-crv-list]').classList.add('sd-open');
      t.remove();
    } else if (t.matches('[data-cfq-all]')) {
      const l = t.closest('.sd-cfq').querySelector('[data-cfq]');
      l.classList.add('sd-open');
      const first = l.querySelector('.sd-cfq-x summary');
      t.remove();
      if (first) first.focus({ preventScroll: true });
    } else if (t.matches('[data-crv-all]')) {
      e.preventDefault();
      const p = panel();
      if (p) openPanel(p, t);
    } else {
      openJm(t);
    }
  });

  // Judge.me on demand: add the widget from its template, load its files once, then press its own button.
  const allReviews = () => {
    const p = document.querySelector('dialog[data-rvs]');
    if (p) openPanel(p);
  };
  const addJm = (jm) => {
    const tpl = jm.querySelector('template[data-crv-jm-tpl]');
    if (!tpl) return;
    jm.append(document.importNode(tpl.content, true));
    tpl.remove();
    // inline data scripts don't run from a template: run them now
    jm.querySelectorAll('script:not([src])').forEach((o) => {
      const n = document.createElement('script');
      n.textContent = o.textContent;
      o.replaceWith(n);
    });
    const j = window.jdgm;
    const w = jm.querySelector('.jdgm-widget[data-entry-point]');
    // On this page only (sunday-jm.css hides Judge.me's header):
    const st = window.jdgmSettings;
    if (st) {
      st.review_widget_header_theme = 'default';
      st.widget_show_histogram = true;
      // one long list that keeps loading as the visitor scrolls (no pages, AR 24 Oct)
      st.widget_pagination_type = 'load_more';
      st.all_reviews_widget_v2025_pagination_type = 'load_more';
    }
    const base = j && j.CDN_BASE_URL;
    if (!w || !base || !j.loadScript) return;
    const src = base + w.dataset.entryPoint;
    if (document.querySelector(`script[src="${src}"]`)) return;
    const css = (href) => {
      if (document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) return;
      const l = document.createElement('link');
      l.rel = 'stylesheet';
      l.href = href;
      document.head.append(l);
    };
    // the widget itself: its CSS from the manifest, then its module
    const widget = () =>
      /* 26 Oct (AR go, network): one manifest per day (was a fresh download every time the reviews opened) */
      fetch(`${base}manifest.json?v=${new Date().toISOString().slice(0, 10)}`)
        .then((r) => r.json())
        .then((m) => {
          const walk = (k) => {
            const c = m[k];
            if (!c) return;
            (c.css || []).forEach((f) => css(base + f));
            (c.imports || []).forEach(walk);
          };
          walk(w.dataset.entryKey);
        })
        .catch(() => {})
        .finally(() => {
          const s = document.createElement('script');
          s.type = 'module';
          s.src = src;
          document.head.append(s);
        });
    // Judge.me's core (base, common, the write-review form): its loader only fetches these when a widget is
    // on the page at load, and the widget gives up on its form if they don't arrive within 10 s. So: core
    // first, then the widget, exactly as the loader would have done at load.
    const coreCss = () => {
      if (j.loadCSS && j.widgetPath) {
        j.loadCSS(j.widgetPath('base.css'));
        j.loadCSS(j.widgetPath('main.css'));
      }
    };
    /* 26 Oct: when warmJm (below) ran the core ahead of the tap, its styles are added now, at the tap */
    if (j.$) {
      if (window.__sdJmBare) coreCss();
      return widget();
    }
    coreCss();
    j.loadScript('widget/base.js', () =>
      j.loadScript('widget/common.js', () => {
        setTimeout(() => j.triggerVanillaEvent && j.triggerVanillaEvent('finishLoadingCore'), 0);
        j.loadScript(`${j.CDN_HOST}widget/write_review_modal.js`);
        widget();
      })
    );
  };
  /* (The stylesheet checks above look for rel="stylesheet" only: the warm-up below leaves rel="prefetch" links with
     the same addresses, which must not count as the style being applied.) */
  /* 26 Oct (AR go): Judge.me ready before the tap. When "Write a review", "Ask a question" or "Read all reviews"
     comes within a screen, once the page is idle: Judge.me's core runs (no widget is on the page, so it draws and
     fetches nothing), and its widget module, styles, manifest and sunday-jm.css are fetched into the cache (not
     applied). The tap then only adds the widget. Nothing is sent from here. */
  const warmJm = () => {
    if (window.__sdJmWarm) return;
    window.__sdJmWarm = true;
    const j = window.jdgm;
    if (!j || !j.loadScript) return;
    const pf = (href) => {
      if (!href || document.querySelector(`link[href="${href}"]`)) return;
      const l = document.createElement('link');
      l.rel = 'prefetch';
      l.href = href;
      document.head.append(l);
    };
    const jm = document.querySelector('[data-crv-jm]');
    if (jm) pf(jm.dataset.jmCss);
    if (j.widgetPath) {
      pf(j.widgetPath('base.css'));
      pf(j.widgetPath('main.css'));
    }
    const tpl = jm && jm.querySelector('template[data-crv-jm-tpl]');
    const w = tpl && tpl.content.querySelector('.jdgm-widget[data-entry-point]');
    const base = j.CDN_BASE_URL;
    if (w && base) {
      const m = document.createElement('link');
      m.rel = 'modulepreload';
      m.href = base + w.dataset.entryPoint;
      document.head.append(m);
      fetch(`${base}manifest.json?v=${new Date().toISOString().slice(0, 10)}`)
        .then((r) => r.json())
        .then((man) => {
          const walk = (k) => {
            const c = man[k];
            if (!c) return;
            (c.css || []).forEach((f) => pf(base + f));
            (c.imports || []).forEach(walk);
          };
          walk(w.dataset.entryKey);
        })
        .catch(() => {});
    }
    if (!j.$) {
      window.__sdJmBare = true;
      j.loadScript('widget/base.js', () =>
        j.loadScript('widget/common.js', () => {
          setTimeout(() => j.triggerVanillaEvent && j.triggerVanillaEvent('finishLoadingCore'), 0);
          j.loadScript(`${j.CDN_HOST}widget/write_review_modal.js`);
        })
      );
    }
  };
  {
    const near = [...document.querySelectorAll('[data-crv-write], [data-crv-ask], [data-crv-all]')];
    if (near.length && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((es) => {
        if (!es.some((e) => e.isIntersecting)) return;
        io.disconnect();
        (window.requestIdleCallback || setTimeout)(warmJm);
      }, { rootMargin: '100% 0px' });
      near.forEach((n) => io.observe(n));
    }
  }
  const openJm = (t) => {
    const wr = t.closest('.sd-wr');
    const sec = t.closest('section');
    const jm = (wr && wr.querySelector('[data-crv-jm]')) || (sec && sec.querySelector('[data-crv-jm]'));
    if (!jm) return allReviews();
    const want = t.matches('[data-crv-write]') ? 'primary' : 'secondary';
    const find = () => jm.querySelector(`.jm-action-buttons__button.jm-button--${want}`);
    const b = find();
    if (b) return b.click();
    addJm(jm);
    t.setAttribute('aria-busy', 'true');
    const start = Date.now();
    const tick = () => {
      const x = find();
      if (x) {
        t.removeAttribute('aria-busy');
        x.click();
      } else if (Date.now() - start > 8000) {
        t.removeAttribute('aria-busy');
        allReviews();
      } else setTimeout(tick, 120);
    };
    tick();
  };

  // ---- Every review (24 Oct 2026, AR) ----
  // Judge.me's list in Sunday's look (sunday-jm.css): in a panel over product pages (snippets/sunday-rv-panel),
  // in place on the reviews page. Star chips/bars press Judge.me's own star filter (its header is hidden).
  // The look; resolves once loaded (at most 2 s), so nothing shows unstyled.
  const css1 = (href) =>
    new Promise((res) => {
      const had = href && document.querySelector(`link[rel="stylesheet"][href="${href}"]`);
      if (!href || (had && had.sheet)) return res();
      const l = had || document.createElement('link');
      l.addEventListener('load', res, { once: true });
      l.addEventListener('error', res, { once: true });
      setTimeout(res, 2000);
      if (!had) {
        l.rel = 'stylesheet';
        l.href = href;
        document.head.append(l);
      }
    });
  const ready = (jm) => jm.querySelector('.jm-review-item, .jm-review-widget__body');
  const load = async (jm) => {
    if (!jm.querySelector('.jdgm-widget')) addJm(jm);
    await css1(jm.dataset.jmCss);
    jm.classList.add('sd-open', 'sd-jm');
    jm.removeAttribute('aria-hidden');
    if (ready(jm)) return;
    jm.setAttribute('aria-busy', 'true');
    await new Promise((res) => {
      const t0 = Date.now();
      const t = () => (ready(jm) || Date.now() - t0 > 15000 ? res() : setTimeout(t, 150));
      t();
    });
    jm.removeAttribute('aria-busy');
  };
  // First load: the most relevant first, or the list's data-sort (the reviews page: photos first).
  const sortFirst = (jm, tries = 0) => {
    const sel = jm.querySelector('.jm-sort-filter select');
    const want = jm.dataset.sort || 'most_relevant';
    if (jm.dataset.sorted) return;
    if (!sel) return tries < 30 && setTimeout(() => sortFirst(jm, tries + 1), 150); // Judge.me draws it a moment later
    jm.dataset.sorted = '1';
    if (sel.value === want || ![...sel.options].some((o) => o.value === want)) return;
    sel.value = want;
    sel.dispatchEvent(new Event('input', { bubbles: true }));
    sel.dispatchEvent(new Event('change', { bubbles: true }));
  };
  // Keep scrolling: Judge.me's "Show more" is pressed for the visitor when it comes within 900 px of the
  // bottom of the screen (or of the panel). Scroll events, not an observer: they also run in a background tab.
  const keepLoading = (jm, root) => {
    if (jm.dataset.more) return;
    jm.dataset.more = '1';
    let busy = 0;
    const check = () => {
      const b = jm.querySelector('.jm-load-more-pagination button');
      if (!b || b.disabled || !jm.offsetParent || Date.now() - busy < 900) return;
      const h = root ? root.getBoundingClientRect().bottom : innerHeight;
      if (b.getBoundingClientRect().top < h + 900) {
        busy = Date.now();
        b.click();
      }
    };
    (root || window).addEventListener('scroll', check, { passive: true });
    new MutationObserver(() => setTimeout(check, 300)).observe(jm, { childList: true, subtree: true });
    check();
  };

  // The panel (product pages)
  let pushed = false;
  const panel = () => document.querySelector('dialog[data-rvs]');
  const openPanel = async (dlg, opener) => {
    const jm = dlg.querySelector('[data-crv-jm]');
    if (!jm.querySelector('.jdgm-widget')) addJm(jm); // Judge.me starts while the look loads
    if (opener) opener.setAttribute('aria-busy', 'true');
    await css1(jm.dataset.jmCss);
    if (opener) opener.removeAttribute('aria-busy');
    const price = document.querySelector('[data-bar-price], [data-ap-price]');
    const size = document.querySelector('[data-chip-label]');
    dlg.querySelector('[data-rvs-price]').textContent = price ? price.textContent.trim() : '';
    dlg.querySelector('[data-rvs-size]').textContent = size ? size.textContent.trim() : '';
    dlg.querySelector('.sd-rvs-ft').hidden = !document.getElementById('add');
    if (!dlg.open) {
      dlg.showModal();
      document.documentElement.classList.add('sd-rvs-lock');
      if (location.hash !== '#reviews-all') {
        history.pushState({ sdRvs: 1 }, '', '#reviews-all'); // the phone's back closes the panel
        pushed = true;
      }
    }
    await load(jm);
    sortFirst(jm);
    keepLoading(jm, dlg.querySelector('[data-rvs-body]'));
  };
  document.addEventListener(
    'close',
    (e) => {
      if (!e.target.matches || !e.target.matches('dialog[data-rvs]')) return;
      document.documentElement.classList.remove('sd-rvs-lock');
      if (pushed) {
        pushed = false;
        history.back();
      } else if (location.hash === '#reviews-all') history.replaceState(history.state, '', location.pathname + location.search);
    },
    true
  );
  addEventListener('popstate', () => {
    const d = panel();
    if (d && d.open && location.hash !== '#reviews-all') {
      pushed = false;
      d.close();
    }
  });
  document.addEventListener('click', (e) => {
    const d = e.target.closest && e.target.closest('dialog[data-rvs]');
    if (!d) return;
    if (e.target === d) return d.close(); // the backdrop
    if (e.target.closest('[data-rvs-x]')) return d.close();
    if (e.target.closest('[data-rvs-add]')) {
      d.close();
      const add = document.getElementById('add');
      if (add) setTimeout(() => add.click(), 80);
      return;
    }
  });

  // A link to #reviews-all opens the panel; the reviews page opens its own list
  const autoOpen = () => {
    const all = document.querySelector('[data-jm-all]');
    if (all) return load(all).then(() => (sortFirst(all), keepLoading(all)));
    const p = panel();
    if (p && location.hash === '#reviews-all' && !p.open) openPanel(p);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', autoOpen);
  else autoOpen();
  addEventListener('hashchange', () => location.hash === '#reviews-all' && autoOpen());

  document.addEventListener('sunday:buy', (e) => {
    const d = e.detail || {};
    if (!d.w) return;
    document.querySelectorAll('[data-csp-weight]').forEach((dd) => {
      let map = {};
      try {
        map = JSON.parse(dd.dataset.cspWeight) || {};
      } catch (err) {}
      const kg = map[String(d.w)];
      if (kg == null) return;
      dd.textContent = fill(dd.dataset.line, { value: kg, size: d.wname || '' });
    });
  });
  if (window.sundayBuyLast) document.dispatchEvent(new CustomEvent('sunday:buy', { detail: window.sundayBuyLast }));
}
